import React, { useEffect, useState } from 'react';
import { Button, Modal, Form, Input, Tag, Popconfirm, message, Select, Card, Spin, Tooltip, Pagination } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CodeOutlined } from '@ant-design/icons';
import { mcpServersApi, dictionariesApi } from '../services/api-services';

const { TextArea } = Input;

const MCPServerList: React.FC = () => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [form] = Form.useForm();
  const [tagOptions, setTagOptions] = useState<{ label: string; value: string }[]>([]);
  const [activeTag, setActiveTag] = useState<string>('');
  const [configItem, setConfigItem] = useState<any>(null);
  const [configForm] = Form.useForm();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const filteredData = activeTag ? data.filter((item: any) => item.tags?.includes(activeTag)) : data;
  const pagedData = filteredData.slice((page - 1) * pageSize, page * pageSize);

  const fetchData = async () => {
    setLoading(true);
    try { const res: any = await mcpServersApi.list(); setData(res); }
    catch (e) { message.error('获取列表失败'); }
    setLoading(false);
  };

  const fetchTags = async () => {
    try {
      const dict: any = await dictionariesApi.getByCode('resource_tag');
      if (dict?.items) setTagOptions(dict.items.map((i: any) => ({ label: i.name, value: i.value })));
    } catch {}
  };

  useEffect(() => { fetchData(); fetchTags(); }, []);

  const handleSave = async (values: any) => {
    try {
      const payload = { ...values, config: editingItem?.config || {}, auth_config: editingItem?.auth_config || {}, available_tools: editingItem?.available_tools || [] };
      if (editingItem) { await mcpServersApi.update(editingItem.id, payload); message.success('更新成功'); }
      else { await mcpServersApi.create(payload); message.success('创建成功'); }
      setModalVisible(false); form.resetFields(); setEditingItem(null); fetchData();
    } catch (e: any) { message.error(e.response?.data?.detail || '操作失败'); }
  };

  const handleSaveConfig = async (values: any) => {
    try {
      let config = undefined;
      if (values.config_str) {
        try { config = JSON.parse(values.config_str); }
        catch { message.error('config 不是合法的 JSON'); return; }
      }
      await mcpServersApi.update(configItem.id, { ...configItem, config });
      message.success('配置已保存');
      setConfigItem(null); configForm.resetFields(); fetchData();
    } catch (e: any) { message.error(e.response?.data?.detail || '保存失败'); }
  };

  const handleDelete = async (id: number) => {
    try { await mcpServersApi.delete(id); message.success('删除成功'); fetchData(); }
    catch (e) { message.error('删除失败'); }
  };

  const openEdit = (record: any) => {
    setEditingItem(record);
    form.setFieldsValue({ identifier: record.identifier, title: record.title, description: record.description, tags: record.tags });
    setModalVisible(true);
  };

  const openConfig = (record: any) => {
    setConfigItem(record);
    configForm.setFieldsValue({ config_str: record.config ? JSON.stringify(record.config, null, 2) : '' });
  };

  return (
    <div className="apple-page">
      <div className="apple-page-header">
        <h1 className="apple-page-title">MCP Server</h1>
        <Button className="apple-btn-create" icon={<PlusOutlined />} onClick={() => { setEditingItem(null); form.resetFields(); setModalVisible(true); }}>新建 MCP Server</Button>
      </div>
      <p className="apple-page-subtitle">管理 Model Context Protocol 服务器配置</p>

      {tagOptions.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
          <Tag style={{ cursor: 'pointer', padding: '4px 12px', fontSize: 13 }} color={activeTag === '' ? 'blue' : undefined} onClick={() => { setActiveTag(''); setPage(1); }}>全部</Tag>
          {tagOptions.map((opt) => (
            <Tag key={opt.value} style={{ cursor: 'pointer', padding: '4px 12px', fontSize: 13 }} color={activeTag === opt.value ? 'blue' : undefined} onClick={() => { setActiveTag(activeTag === opt.value ? '' : opt.value); setPage(1); }}>{opt.label}</Tag>
          ))}
        </div>
      )}

      <Spin spinning={loading}>
        <div className="mcp-card-grid">
          {pagedData.map((item: any) => (
            <div key={item.id}>
              <Card
                hoverable
                style={{ height: '100%', position: 'relative' }}
                actions={[
                  <Button key="config" type="link" size="small" icon={<CodeOutlined />} onClick={() => openConfig(item)}>配置</Button>,
                  <Popconfirm key="del" title="确定删除?" onConfirm={() => handleDelete(item.id)}>
                    <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
                  </Popconfirm>,
                ]}
                title={
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }} className="mcp-card-header">
                    <span style={{ fontWeight: 600, fontSize: 15 }}>{item.title}</span>
                    <Button
                      className="mcp-edit-btn"
                      type="text" size="small" icon={<EditOutlined />}
                      style={{ opacity: 0, transition: 'opacity 0.2s' }}
                      onClick={() => openEdit(item)}
                    />
                  </div>
                }
              >
                {item.tags?.length > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    {item.tags.map((t: string) => <Tag key={t}>{tagOptions.find(o => o.value === t)?.label || t}</Tag>)}
                  </div>
                )}
                {item.description && (
                  <div style={{ fontSize: 13, marginBottom: 8 }}>
                    <span style={{ color: 'var(--muted-text)' }}>描述：</span>{item.description.length > 50 ? <Tooltip title={item.description}>{item.description.slice(0, 50)}...</Tooltip> : item.description}
                  </div>
                )}
                <div style={{ fontSize: 13, marginBottom: 8 }}>
                  <span style={{ color: 'var(--muted-text)' }}>版本：</span>
                  <span className="apple-tag">v{item.version || 1}</span>
                </div>
              </Card>
            </div>
          ))}
          {!loading && filteredData.length === 0 && (
            <div style={{ textAlign: 'center', color: 'var(--muted-text)', padding: '48px 0', gridColumn: '1 / -1' }}>暂无数据</div>
          )}
        </div>
        {filteredData.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
            <Pagination current={page} pageSize={pageSize} total={filteredData.length} onChange={(p) => setPage(p)} showSizeChanger pageSizeOptions={['10', '20', '50', '100']} onShowSizeChange={(_, size) => { setPageSize(size); setPage(1); }} showTotal={(t) => `共 ${t} 条`} />
          </div>
        )}
      </Spin>

      {/* 新建/编辑弹窗（不含配置） */}
      <Modal
        className="apple-modal"
        title={editingItem ? '编辑 MCP Server' : '新建 MCP Server'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={600}
      >
        <Form className="apple-form" form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item name="identifier" label="标识" rules={[{ required: true }, { pattern: /^[a-z0-9-]+$/, message: '仅支持小写字母、数字和连字符' }, { max: 64, message: '最长64字符' }]}><Input placeholder="如 my-mcp-server" /></Form.Item>
          <Form.Item name="title" label="名称" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="description" label="描述"><TextArea rows={3} /></Form.Item>
          <Form.Item name="tags" label="标签">
            <Select mode="multiple" placeholder="选择标签" options={tagOptions} allowClear />
          </Form.Item>
        </Form>
      </Modal>

      {/* 配置弹窗 */}
      <Modal
        className="apple-modal"
        title={`配置 — ${configItem?.title}`}
        open={!!configItem}
        onCancel={() => { setConfigItem(null); configForm.resetFields(); }}
        onOk={() => configForm.submit()}
        width={700}
      >
        <Form form={configForm} layout="vertical" onFinish={handleSaveConfig}>
          <Form.Item name="config_str" label="配置（JSON 格式）" rules={[{ validator: (_, v) => { if (!v) return Promise.resolve(); try { JSON.parse(v); return Promise.resolve(); } catch { return Promise.reject('请输入合法的 JSON'); } } }]}>
            <TextArea rows={12} placeholder={'{\n  "key": "value"\n}'} style={{ fontFamily: 'monospace' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MCPServerList;
